import { supabase } from "@/integrations/supabase/client";

/**
 * QA-DATA-INSIGHTS-02 — FIFA code → api-football team id resolver.
 *
 * VerseSport identifies national teams by FIFA codes (ARG, BRA, MEX, …)
 * but api-football expects internal numeric IDs (Argentina=26, Brazil=6, …).
 * Without this translation step every /fixtures/headtohead call returns an
 * empty response and the panel cannot display real history.
 */

export interface ProviderTeamMapping {
  fifa_code: string;
  fifa_name: string;
  api_football_team_id: number | null;
  api_football_name: string | null;
  enabled: boolean;
}

let cache: Map<string, ProviderTeamMapping> | null = null;
let inflight: Promise<Map<string, ProviderTeamMapping>> | null = null;

async function load(): Promise<Map<string, ProviderTeamMapping>> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    const { data, error } = await supabase
      .from("national_teams_provider_map" as never)
      .select("fifa_code,fifa_name,api_football_team_id,api_football_name,enabled");
    if (error) {
      console.warn("[TEAM_MAP_LOAD_ERR]", { error: error.message });
      return new Map();
    }
    const m = new Map<string, ProviderTeamMapping>();
    for (const row of (data ?? []) as ProviderTeamMapping[]) {
      m.set(row.fifa_code.toUpperCase(), row);
    }
    cache = m;
    return m;
  })();
  const r = await inflight;
  inflight = null;
  return r;
}

export async function resolveProviderTeamId(fifaCode: string): Promise<{
  fifa_code: string;
  api_football_team_id: number | null;
  api_football_name: string | null;
  enabled: boolean;
  found: boolean;
}> {
  const code = fifaCode.toUpperCase();
  const map = await load();
  const row = map.get(code);
  if (!row) {
    return { fifa_code: code, api_football_team_id: null, api_football_name: null, enabled: false, found: false };
  }
  return {
    fifa_code: code,
    api_football_team_id: row.api_football_team_id,
    api_football_name: row.api_football_name,
    enabled: row.enabled,
    found: true,
  };
}

export async function resolveH2HPair(homeCode: string, awayCode: string) {
  const [home, away] = await Promise.all([
    resolveProviderTeamId(homeCode),
    resolveProviderTeamId(awayCode),
  ]);
  const missing: string[] = [];
  if (!home.found || !home.enabled || home.api_football_team_id == null) missing.push(home.fifa_code);
  if (!away.found || !away.enabled || away.api_football_team_id == null) missing.push(away.fifa_code);
  return { home, away, missing };
}